/**
 * Shared values for the `FetchSkeleton*` loading primitives.
 *
 * Every value here is lifted from the customer dashboard and admin v2 skeletons that already ship
 * (`EventsPageSkeleton`, `EventCardSkeleton`, `AdminAllOrgsPageSkeleton`, `AdminUsersPageSkeleton`,
 * `AdminOrgCardSkeleton`, `AdminEventRowSkeleton`), so a page built from the primitives reads the
 * same as those: gray-100 bars, gray-200 for the one line that carries a row's name, 4px corners
 * on text, full rounding on pills and avatars, a slow opacity pulse.
 */

/** Screen-reader text for the one `role="status"` region that wraps a page's skeleton. Never shown. */
export const SKELETON_SCREEN_READER_LABEL = "Loading";

/**
 * The pulse. `motion-safe:` so a viewer who asked the OS for reduced motion sees still shapes.
 * Kept off the bare `animate-pulse` on purpose: the plain class ignores that setting.
 */
export const SKELETON_PULSE_CLASS = "motion-safe:animate-pulse";

/** Fill per tone. `strong` is for the one line that stands for a name or title; `base` for the rest. */
export const SKELETON_TONE_CLASS = {
  base: "bg-gray-100",
  strong: "bg-gray-200",
} as const;

/** A skeleton block's tone. */
export type SkeletonTone = keyof typeof SKELETON_TONE_CLASS;

/** Corner radius per block shape, matching what each shape stands in for. */
export const SKELETON_SHAPE_CLASS = {
  /** A line of text. */
  text: "rounded",
  /** A generic block: an input, an image band, a chart. */
  block: "rounded-lg",
  /** A FetchButton (`rounded-xl`). */
  button: "rounded-xl",
  /** A pill, chip, or tag. */
  pill: "rounded-full",
  /** A round avatar. */
  circle: "rounded-full",
  /** A square avatar or organization icon. */
  square: "rounded-lg",
  /** A card (`rounded-3xl`, the admin and customer card radius). */
  card: "rounded-3xl",
} as const;

/** A skeleton block's shape. */
export type SkeletonShape = keyof typeof SKELETON_SHAPE_CLASS;

/**
 * Per text size: the line box (the real text's line height, so a skeleton line takes exactly the
 * room the text will) and the bar drawn inside it.
 */
export const SKELETON_TEXT_SIZE_CLASS = {
  xs: { line: "h-4", bar: "h-2.5" },
  sm: { line: "h-5", bar: "h-3.5" },
  base: { line: "h-6", bar: "h-4" },
  lg: { line: "h-7", bar: "h-4" },
  "3xl": { line: "h-9", bar: "h-7" },
} as const;

/** A skeleton text line's size, named after the Tailwind text size it stands in for. */
export type SkeletonTextSize = keyof typeof SKELETON_TEXT_SIZE_CLASS;

/** Line widths, cycled so a stack of lines or rows does not read as one stamped shape. */
export const SKELETON_LINE_WIDTHS = ["w-3/4", "w-1/2", "w-2/3", "w-2/5"] as const;

/** Avatar sizes: `sm` is a table owner avatar, `md` an org card icon, `lg` a profile header. */
export const SKELETON_AVATAR_SIZE_CLASS = {
  sm: "size-7",
  md: "size-9",
  lg: "size-10",
} as const;

/** A skeleton avatar's size. */
export type SkeletonAvatarSize = keyof typeof SKELETON_AVATAR_SIZE_CLASS;

/** Pill sizes: `sm` is a status pill in a table, `md` a chip or tag. */
export const SKELETON_PILL_SIZE_CLASS = {
  sm: "h-5 w-14",
  md: "h-6 w-20",
} as const;

/** A skeleton pill's size. */
export type SkeletonPillSize = keyof typeof SKELETON_PILL_SIZE_CLASS;

/** A FetchButton at `size="sm"`, the size every page header and toolbar action uses. */
export const SKELETON_BUTTON_CLASS = "h-9 w-32";

/** The toolbar's Filters pill (`filterTriggerClassName`: `px-4 py-2 text-sm`). */
export const SKELETON_FILTER_PILL_CLASS = "h-9 w-24";

/**
 * The toolbar search box's resting chrome (`FetchInput` with the admin toolbar's rounded-full
 * gray-50 classes), drawn empty: the outline is chrome, not data, so it does not pulse.
 */
export const SKELETON_SEARCH_CLASS =
  "h-[42px] min-w-[200px] max-w-sm flex-1 rounded-full border border-gray-200 bg-gray-50 sm:h-[38px]";

/** One slider tab (`FetchTabBar variant="slider"`: `px-4 py-3 text-sm`) with its gray underline. */
export const SKELETON_TAB_CLASS = "relative flex items-center px-4 py-3";

/** The slider tab bar's always-on gray underline, drawn under each skeleton tab. */
export const SKELETON_TAB_UNDERLINE_CLASS = "absolute inset-x-0 bottom-0 h-0.5 bg-gray-200";

/** Width of a skeleton tab's label bar. */
export const SKELETON_TAB_LABEL_CLASS = "w-16";

/** A table cell's padding (`TableCell`: `p-4`) and a header cell's height (`TableHead`: `h-12`). */
export const SKELETON_TABLE_CELL_CLASS = "p-4 align-middle";

/** A skeleton table's header cell: the real `TableHead` height and padding. */
export const SKELETON_TABLE_HEAD_CLASS = "h-12 px-4 text-left align-middle";

/** Default number of body rows in a `FetchSkeletonTable`: enough to fill a first screen. */
export const SKELETON_TABLE_DEFAULT_ROW_COUNT = 8;

/** Default number of text lines under a `FetchSkeletonCard`'s header. */
export const SKELETON_CARD_DEFAULT_LINE_COUNT = 3;

/** A `FetchSkeletonCard`'s photo band height (`EventCard`'s 200px image). */
export const SKELETON_CARD_MEDIA_CLASS = "h-[200px]";
